import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  useToast
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { createStaticPage, getStaticPageData, updateStaticPageData } from '../../services/StaticPages';
import FormLoader from '../../CommonComponents/FormLoader';
import { handleApiError, showErrorToast, showSuccessToast } from '../../util/errorHandler';
import CKEditorWidget from '../../CommonComponents/CKEditorWidget';

// Schema for the form
const formSchema = (slug) => ({
  "title": "",
  "type": "object",
  "properties": {
    "_id": {
      "type": "string",
      "title": "_id"
    },
    "slug": {
      "type": "string",
      "title": "Slug",
      "default": ""
    },
    "title": {
      "type": "string",
      "title": "Title",
      "default": ""
    },
    "path": {
      "type": "string",
      "title": "Path",
      "default": ""
    },
    "parentPath": {
      "type": "string",
      "title": "Parent Path",
      "default": ""
    },
    "status": {
      "type": "string",
      "title": "Status",
      "enum": ["draft", "published"],
      "default": "draft"
    },
    "language": {
      "type": "string",
      "title": "Language",
      "enum": ["en", "hin"],
      "default": "en"
    },
    "format": {
      "type": "string",
      "title": "Format",
      "default": "html"
    },
    "content_html": {
      "type": "string",
      "title": "Content",
      "default": ""
    },
    "metaTitle": {
      "type": "string",
      "title": "Meta Title",
      "default": ""
    },
    "metaDescription": {
      "type": "string",
      "title": "Meta Description",
      "default": ""
    }
  },
  "required": ["slug", "title", "language", "content_html", "path"]
});

// UI Schema
const formUiSchema = (slug, path, isSubmitting) => ({
  "ui:submitButtonOptions": {
    "norender": true
  },
  "ui:style": {
    fontSize: "14px",
  },
  "_id": {
    "ui:widget": "hidden"
  },
  slug: {
    "ui:emptyValue": "",
    "ui:readonly": !!slug,
    "ui:disabled": !!slug,
  },
  title: {
    "ui:emptyValue": ""
  },
  path: {
    "ui:emptyValue": "" ,
    "ui:readonly": !!slug,
    "ui:disabled": !!slug,
  },
  parentPath: {
    "ui:emptyValue": ""
  },
  language: {
    "ui:emptyValue": "en",
    "ui:enumNames": ["English", "Hindi"],
  },
  status: {
    "ui:enumNames": ["Draft", "Published"],
  },
  format: {
    "ui:emptyValue": "html"
  },
  content_html: {
    "ui:widget": CKEditorWidget,
    "ui:options": {
      height: "400px",
      placeholder: "Enter Content"
    }
  },
  metaTitle: {
    "ui:emptyValue": ""
  },
  metaDescription: {
    "ui:widget": "textarea",
    "ui:emptyValue": ""
  }
});

const PublicDetailsForm = () => {
  const { slug } = useParams();
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const session = useSelector(state => state.session);
  const userId = session?.user?._id || session?.user?.user?._id;

  const widgets = {
    CKEditorWidget: CKEditorWidget
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (slug) {
          const result = await getStaticPageData(slug);
          if (result?.data) {
            const data = result.data;
            
            // Set form data
            setFormData({
              _id: data._id,
              slug: data.slug,
              title: data.title || "",
              path: data.path || "",
              parentPath: data.parentPath || "",
              status: data.status || "draft",
              language: data.language || "en",
              format: data.content?.format || "html",
              content_html: data.content?.html || "",
              metaTitle: data.seo?.metaTitle || "",
              metaDescription: data.seo?.metaDescription || ""
            });
            
            setQuickSave(false);
          }
        } else {
          // For new pages, initialize empty form data
          setFormData({
            slug: "",
            title: "",
            path: "",
            parentPath: "",
            status: "draft",
            language: "en",
            format: "html",
            content_html: "",
            metaTitle: "",
            metaDescription: ""
          });
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data"
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, quickSave]);

  // Handle form changes
  const handleFormChange = (data) => {
    setFormData(data.formData);
  };

  // Submit updated data
  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.slug || !formData.title || !formData.content_html || !formData.language) {
      showErrorToast(toast, "Slug, Title, Language, and Content are required fields.");
      return;
    }

    // Prepare payload
    const payload = {
      slug: formData.slug,
      title: formData.title,
      path: formData.path,
      parentPath: formData.parentPath || null,
      status: formData.status || "draft",
      language: formData.language,
      content: {
        format: formData.format || "html",
        html: formData.content_html
      }
    };

    // Add _id if editing
    if (formData._id) {
      payload._id = formData._id;
    }

    // Add createdBy for new records
    if (!slug && userId) {
      payload.createdBy = userId;
    }

    // Add seo fields only if they have values
    if (formData.metaTitle || formData.metaDescription) {
      payload.seo = {
        metaTitle: formData.metaTitle || "",
        metaDescription: formData.metaDescription || ""
      };
    }

    try {
      setIsSubmitting(true);
      
      if (slug) {
        // Update existing page
        const result = await updateStaticPageData(slug, payload);
        if (result?.status === 200) {
          setQuickSave(prev => !prev);
          showSuccessToast(toast, "Page updated successfully");
        }
      } else {
        // Create new page
        const result = await createStaticPage(payload);
        if (result?.status === 201) {
          setQuickSave(prev => !prev);
          showSuccessToast(toast, "Page created successfully");
          navigate(`/public/${result?.data?.slug}`);
        }
      }
    } catch (error) {
      handleApiError(error, toast);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          <Box bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
            <Form
              schema={formSchema(slug)}
              uiSchema={formUiSchema(slug, formData.path, isSubmitting )}
              formData={formData}
              validator={validator}
              onChange={handleFormChange}
              widgets={widgets}
              fontsize={"14px"}
            />
          </Box>
        </Box>
      )}
      
      {/* Fixed Save Button at Bottom */}
      <Box
        position="fixed"
        bottom={0}
        left="250px"
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
        display="flex"
      >
        <Box maxW="auto" display="flex" mx="auto">
          <Button
            colorScheme="blue"
            size="lg"
            width="auto"
            isLoading={isSubmitting}
            loadingText="Saving..."
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
      
      {/* Add padding to prevent content from being hidden behind fixed button */}
      <Box pb="100px" />
    </Box>
  );
};

export default PublicDetailsForm;
